"use client";

import { ComponentInfo } from "@builder.io/sdk-react";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";

export const STANDARD_PRODUCT_DETAILS_CONTAINER_INPUTS = [
  {
    name: "hasDiscountBadge",
    type: "boolean",
  },
  {
    name: "hasStrikethroughPrice",
    type: "boolean",
  },
  {
    name: "hideCta",
    type: "boolean",
  },
  {
    name: "hidePrice",
    type: "boolean",
  },
  {
    name: "isTradeInDisclaimerVisible",
    type: "boolean",
    defaultValue: false,
    helperText: "Show Trade In Disclaimer below the Add to cart CTA",
  },
  {
    name: "tradeInDisclaimerData",
    type: "object",
    showIf: `options.get("isTradeInDisclaimerVisible") === true`,
    helperText: "Inputs for Trade In disclaimer",
    subFields: [
      {
        name: "title",
        type: "string",
      },
      {
        name: "description",
        type: "string",
      },
      {
        name: "ctaText",
        type: "string",
      },
      {
        name: "ctaLink",
        type: "string",
        helperText: "Link to corresponding Trade In PDP",
      },
    ],
  },
  {
    name: "deviceRegistrationConfiguration",
    type: "object",
    helperText: "Inputs for Device Registration Dialog",
    subFields: [
      {
        name: "theme",
        type: "enum",
        enum: ["light", "dark"],
        defaultValue: "light",
      },
      {
        name: "is12DeviceCodeLength",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "is15DeviceCodeLength",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "is16DeviceCodeLength",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "registerDeviceImgDesktop",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "registerDeviceImgMobile",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "registerDeviceImgAlt",
        type: "string",
      },
      {
        name: "registerDeviceDialogTitle",
        type: "string",
      },
      {
        name: "registerDeviceDialogDescription",
        type: "string",
      },
      {
        name: "registerDeviceAccordionTitle",
        type: "string",
      },
      {
        name: "registerDeviceAccordionButtonUrl",
        type: "url",
      },
      {
        name: "registerDeviceAccordionButtonText",
        type: "string",
      },
      {
        name: "cancelDeviceRegistrationButtonText",
        type: "string",
      },
      {
        name: "confirmDeviceRegistrationButtonText",
        type: "string",
      },
      {
        name: "accordionImageOne",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "accordionImageOneAlt",
        type: "string",
      },
      {
        name: "accordionImageOneDescription",
        type: "string",
      },
      {
        name: "accordionImageTwo",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "accordionImageTwoAlt",
        type: "string",
      },
      {
        name: "accordionImageTwoDescription",
        type: "string",
      },
    ],
  },
] as const satisfies ComponentInfo["inputs"];

export const STANDARD_PRODUCT_DETAILS_CONTAINER_BINDINGS_INPUTS = [
  {
    name: "sku",
    type: "string",
  },
  {
    name: "productName",
    type: "string",
  },
  {
    name: "description",
    type: "string",
  },
  {
    name: "currentVariantId",
    type: "string",
  },
  {
    name: "isBundle",
    type: "boolean",
  },
  {
    name: "multiPackProducts",
    type: "list",
  },
  {
    name: "bundlesConfig",
    type: "list",
  },
  {
    name: "categories",
    type: "list",
    subFields: [
      {
        name: "name",
        type: "string",
        required: true,
      },
      {
        name: "id",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "isTrial",
    type: "boolean",
  },
  {
    name: "configurableOptions",
    type: "list",
  },
  {
    name: "dynamicGallery",
    type: "list",
  },
  {
    name: "videoGallery",
    type: "list",
  },
  {
    name: "trustpilot",
    type: "object",
    subFields: [
      {
        name: "isEnabled",
        type: "boolean",
      },
    ],
  },
  {
    name: "productCta",
    type: "object",
    subFields: [
      {
        name: "label",
        type: "string",
      },
      {
        name: "url",
        type: "string",
      },
    ],
  },
  {
    name: "multiPackName",
    type: "string",
  },
  {
    name: "multiPackDescription",
    type: "string",
  },
  {
    name: "galleryAssets",
    type: "list",
    subFields: [
      {
        name: "path",
        type: "string",
        required: true,
      },
      {
        name: "type",
        type: "string",
        enum: ["image", "video"],
        required: true,
        defaultValue: "image",
      },
      {
        name: "disabled",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "previewPath",
        type: "string",
      },
      {
        name: "label",
        type: "string",
      },
      {
        name: "position",
        type: "number",
      },
    ],
  },
  {
    name: "isMultiPackEnabled",
    type: "boolean",
  },
  {
    name: "knowMoreUrl",
    type: "string",
  },
  {
    name: "isDynamicGalleryEnabled",
    type: "boolean",
  },
  {
    name: "currentVariantSku",
    type: "string",
  },
  {
    name: "nonTradeInSku",
    type: "string",
  },
] as const satisfies ComponentInfo["inputs"];

export const ALL_STANDARD_PRODUCT_DETAILS_CONTAINER_INPUTS = [
  ...STANDARD_PRODUCT_DETAILS_CONTAINER_INPUTS,
  ...STANDARD_PRODUCT_DETAILS_CONTAINER_BINDINGS_INPUTS,
] as const satisfies ComponentInfo["inputs"];

export type StandardProductDetailsContainerProps = BuilderComponentProps<
  typeof ALL_STANDARD_PRODUCT_DETAILS_CONTAINER_INPUTS
>;
